import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import {
  fetchRequirementById,
  updateRequirement,
  fetchCandidates
} from '../../store/slices/staffingSlice';
import Layout from '../../components/Layout';
import Loading from '../../components/Loading';
import StatusBadge from '../../components/StatusBadge';
import CommentSection from '../../components/CommentSection';
import StatusHistory from '../../components/StatusHistory';
import { STAFFING_REQUIREMENT_STATUS, COMMENT_ENTITY_TYPES } from '../../utils/constants';
import { formatDateTime } from '../../utils/formatDate';

const StaffingRequirementDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { currentRequirement, candidates, loading } = useSelector((state) => state.staffing);
  const [editing, setEditing] = useState(false);
  const [updating, setUpdating] = useState(false);
  const [formData, setFormData] = useState({});

  useEffect(() => {
    if (id && id !== 'new') {
      dispatch(fetchRequirementById(id));
      dispatch(fetchCandidates({ requirement_id: id }));
    }
  }, [dispatch, id]);

  useEffect(() => {
    if (currentRequirement) {
      setFormData({
        title: currentRequirement.title,
        skills_required: Array.isArray(currentRequirement.skills_required)
          ? currentRequirement.skills_required.join(', ')
          : currentRequirement.skills_required,
        experience_required: currentRequirement.experience_required,
        number_of_positions: currentRequirement.number_of_positions,
        client_budget: currentRequirement.client_budget || '',
        status: currentRequirement.status
      });
    }
  }, [currentRequirement]);

  const handleChange = (e) => {
    const value = e.target.type === 'number' ? parseInt(e.target.value) : e.target.value;
    setFormData({
      ...formData,
      [e.target.name]: value
    });
  };

  const handleUpdate = async (e) => {
    e.preventDefault();
    setUpdating(true);

    try {
      const submitData = {
        ...formData,
        skills_required: typeof formData.skills_required === 'string'
          ? formData.skills_required.split(',').map(s => s.trim()).filter(s => s)
          : formData.skills_required
      };

      await dispatch(updateRequirement({ id, data: submitData })).unwrap();
      dispatch(fetchRequirementById(id));
      setEditing(false);
    } catch (error) {
      alert('Failed to update requirement: ' + error);
    } finally {
      setUpdating(false);
    }
  };

  if (loading || !currentRequirement) {
    return (
      <Layout>
        <Loading />
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="container-fluid">
        <button
          className="btn btn-outline-secondary mb-3"
          onClick={() => navigate(`/staffing/clients/${currentRequirement.client_id?._id || currentRequirement.client_id}`)}
        >
          <i className="ti ti-arrow-left me-1" />
          Back to Client
        </button>

        <div className="card mb-3">
          <div className="card-body">
            {editing ? (
              <form onSubmit={handleUpdate}>
                <h2 className="fs-5 mb-3">Edit Requirement</h2>

                <div className="mb-3">
                  <label className="form-label">Position Title *</label>
                  <input
                    type="text"
                    name="title"
                    className="form-control"
                    value={formData.title}
                    onChange={handleChange}
                    required
                  />
                </div>

                <div className="mb-3">
                  <label className="form-label">Skills Required *</label>
                  <input
                    type="text"
                    name="skills_required"
                    className="form-control"
                    value={formData.skills_required}
                    onChange={handleChange}
                    required
                  />
                  <small className="text-muted d-block mt-1">
                    Enter skills separated by commas
                  </small>
                </div>

                <div className="mb-3">
                  <label className="form-label">Experience Required *</label>
                  <input
                    type="text"
                    name="experience_required"
                    className="form-control"
                    value={formData.experience_required}
                    onChange={handleChange}
                    required
                  />
                </div>

                <div className="mb-3">
                  <label className="form-label">Number of Positions *</label>
                  <input
                    type="number"
                    name="number_of_positions"
                    className="form-control"
                    value={formData.number_of_positions}
                    onChange={handleChange}
                    min="1"
                    required
                  />
                </div>

                <div className="mb-3">
                  <label className="form-label">Client Budget</label>
                  <input
                    type="text"
                    name="client_budget"
                    className="form-control"
                    value={formData.client_budget}
                    onChange={handleChange}
                    placeholder="e.g., $50,000 - $70,000"
                  />
                </div>

                <div className="mb-3">
                  <label className="form-label">Status *</label>
                  <select
                    name="status"
                    className="form-select"
                    value={formData.status}
                    onChange={handleChange}
                    required
                  >
                    {Object.values(STAFFING_REQUIREMENT_STATUS).map((status) => (
                      <option key={status} value={status}>
                        {status.replace(/_/g, ' ')}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="d-flex gap-2">
                  <button type="button" className="btn btn-outline-secondary" onClick={() => setEditing(false)}>
                    Cancel
                  </button>
                  <button type="submit" className="btn btn-primary" disabled={updating}>
                    {updating ? 'Saving...' : 'Save Changes'}
                  </button>
                </div>
              </form>
            ) : (
              <>
                <div className="d-flex justify-content-between align-items-start">
                  <div>
                    <h2 className="fs-4 mb-2">{currentRequirement.title}</h2>
                    <StatusBadge status={currentRequirement.status} />
                  </div>
                  <button className="btn btn-outline-primary" onClick={() => setEditing(true)}>
                    Edit
                  </button>
                </div>

                <div className="row g-3 mt-3">
                  <div className="col-12 col-sm-6">
                    <label className="text-muted small fw-semibold">SKILLS REQUIRED</label>
                    <p className="mb-0">{Array.isArray(currentRequirement.skills_required)
                      ? currentRequirement.skills_required.join(', ')
                      : currentRequirement.skills_required}</p>
                  </div>
                  <div className="col-12 col-sm-6">
                    <label className="text-muted small fw-semibold">EXPERIENCE REQUIRED</label>
                    <p className="mb-0">{currentRequirement.experience_required}</p>
                  </div>
                  <div className="col-12 col-sm-6">
                    <label className="text-muted small fw-semibold">POSITIONS</label>
                    <p className="mb-0">{currentRequirement.number_of_positions}</p>
                  </div>
                  <div className="col-12 col-sm-6">
                    <label className="text-muted small fw-semibold">CLIENT BUDGET</label>
                    <p className="mb-0">{currentRequirement.client_budget || 'Not specified'}</p>
                  </div>
                  <div className="col-12 col-sm-6">
                    <label className="text-muted small fw-semibold">CLIENT</label>
                    <p className="mb-0">{currentRequirement.client_id?.name || 'N/A'}</p>
                  </div>
                  <div className="col-12 col-sm-6">
                    <label className="text-muted small fw-semibold">CREATED</label>
                    <p className="mb-0">{formatDateTime(currentRequirement.created_at)}</p>
                  </div>
                </div>
              </>
            )}
          </div>
        </div>

        {/* Candidates Section */}
        <div className="card table-responsive mb-3">
          <div className="card-header bg-white d-flex justify-content-between align-items-center">
            <h3 className="fs-6 mb-0">Candidates</h3>
            <button
              className="btn btn-sm btn-primary"
              onClick={() => navigate(`/staffing/requirements/${id}/candidates/new`)}
            >
              <i className="ti ti-plus me-1" />
              Add Candidate
            </button>
          </div>

          <table className="table mb-0 text-nowrap table-hover">
            <thead className="table-light border-light">
              <tr>
                <th>Name</th>
                <th>Skills</th>
                <th>Experience</th>
                <th>Status</th>
                <th>Created</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {candidates.length === 0 ? (
                <tr>
                  <td colSpan={6} className="text-center py-4 text-muted">
                    No candidates yet
                  </td>
                </tr>
              ) : (
                candidates.map((candidate) => (
                  <tr key={candidate._id} className="align-middle">
                    <td className="fw-medium">{candidate.name}</td>
                    <td>{Array.isArray(candidate.skills) ? candidate.skills.join(', ') : candidate.skills}</td>
                    <td>{candidate.experience}</td>
                    <td><StatusBadge status={candidate.status} /></td>
                    <td>{formatDateTime(candidate.created_at)}</td>
                    <td>
                      <button
                        className="btn btn-sm btn-outline-primary"
                        onClick={() => navigate(`/staffing/candidates/${candidate._id}`)}
                      >
                        View
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        <StatusHistory history={currentRequirement.status_history} />

        {/* Comments Section */}
        <CommentSection
          entityType={COMMENT_ENTITY_TYPES.STAFFING_REQUIREMENT}
          entityId={id}
        />
      </div>
    </Layout>
  );
};

export default StaffingRequirementDetail;
